import React, { useCallback, useEffect, useState } from 'react'
import {
  RefreshCw,
  LayoutDashboard,
  LineChart,
  FileSpreadsheet,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { DateInput } from '@/components/forms/DateInput'
import { OperationalDashboardTab } from './OperationalDashboardTab'
import { ManagementDashboardTab } from './ManagementDashboardTab'
import { DetailedReportsTab } from './DetailedReportsTab'
import {
  DATE_PRESET_LABELS,
  MATERIAL_CATEGORY_LABELS,
  getDetailedReportsApi,
  getManagementDashboardApi,
  getOperationalDashboardApi,
  type DatePreset,
  type DetailedReportType,
  type DetailedReportsData,
  type ManagementDashboardData,
  type MaterialCategory,
  type OperationalDashboardData,
} from '@/services/reports/inventoryDashboardApi'

export type DashboardTabId = 'operational' | 'management' | 'detailed'

export function InventoryDashboardPage(): React.ReactElement {
  // Navigation State
  const [activeTab, setActiveTab] = useState<DashboardTabId>('operational')

  // Common Filter States
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('last_30_days')
  const [selectedCategory, setSelectedCategory] = useState<MaterialCategory>('ALL')
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo, setCustomDateTo] = useState('')

  // Detailed Report Selection
  const [selectedDetailedType, setSelectedDetailedType] =
    useState<DetailedReportType>('stock_available')

  // Data States
  const [operationalData, setOperationalData] = useState<OperationalDashboardData | null>(null)
  const [managementData, setManagementData] = useState<ManagementDashboardData | null>(null)
  const [detailedData, setDetailedData] = useState<DetailedReportsData | null>(null)

  // Loading States
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch data for the active view
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const filterParams = {
        category: selectedCategory,
        preset: selectedPreset,
        dateFrom: selectedPreset === 'custom' ? customDateFrom : undefined,
        dateTo: selectedPreset === 'custom' ? customDateTo : undefined,
      }

      if (activeTab === 'operational') {
        const res = await getOperationalDashboardApi({ category: selectedCategory })
        setOperationalData(res)
      } else if (activeTab === 'management') {
        const res = await getManagementDashboardApi(filterParams)
        setManagementData(res)
      } else if (activeTab === 'detailed') {
        const res = await getDetailedReportsApi({
          ...filterParams,
          reportType: selectedDetailedType,
        })
        setDetailedData(res)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải dữ liệu dashboard kho')
    } finally {
      setLoading(false)
    }
  }, [
    activeTab,
    selectedCategory,
    selectedPreset,
    customDateFrom,
    customDateTo,
    selectedDetailedType,
  ])

  useEffect(() => {
    void loadData()
  }, [loadData])

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Báo cáo', to: '/reports' },
          { label: 'Dashboard Kho' },
        ]}
      />

      {/* Page Header */}
      <PageHeader
        title="Dashboard Quản Trị & Vận Hành Kho"
        subtitle="Theo dõi tồn kho, biến động nhập xuất, giữ hàng và chuyển mã"
      />

      {/* Common Filter Toolbar */}
      <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Left filters: Nhóm vật tư + Presets thời gian */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Nhóm vật tư */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase text-[var(--color-text-muted)] whitespace-nowrap">
                Nhóm VT:
              </span>
              <select
                aria-label="Nhóm vật tư"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as MaterialCategory)}
                className="h-8.5 rounded border border-[var(--color-border)] bg-white px-2.5 text-xs font-medium text-slate-800 focus:border-[var(--color-accent)] focus:outline-hidden"
              >
                {(Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]).map((cat) => (
                  <option key={cat} value={cat}>
                    {MATERIAL_CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
            </div>

            {/* Presets thời gian (Hiển thị cho tab Management và Detailed, hoặc lọc chung) */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase text-[var(--color-text-muted)] whitespace-nowrap">
                Kỳ:
              </span>
              <select
                aria-label="Kỳ thời gian"
                value={selectedPreset}
                onChange={(e) => setSelectedPreset(e.target.value as DatePreset)}
                className="h-8.5 rounded border border-[var(--color-border)] bg-white px-2.5 text-xs font-medium text-slate-800 focus:border-[var(--color-accent)] focus:outline-hidden"
              >
                {(Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((preset) => (
                  <option key={preset} value={preset}>
                    {DATE_PRESET_LABELS[preset]}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom date range with DateInput */}
            {selectedPreset === 'custom' && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[var(--color-text-muted)]">Từ:</span>
                <DateInput
                  aria-label="Từ ngày"
                  value={customDateFrom}
                  onChange={setCustomDateFrom}
                  placeholder="dd/mm/yyyy"
                  className="w-28 text-xs"
                />
                <span className="text-xs text-[var(--color-text-muted)]">Đến:</span>
                <DateInput
                  aria-label="Đến ngày"
                  value={customDateTo}
                  onChange={setCustomDateTo}
                  placeholder="dd/mm/yyyy"
                  className="w-28 text-xs"
                />
              </div>
            )}
          </div>

          {/* Right: Refresh button */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void loadData()}
              disabled={loading}
              title="Làm mới dữ liệu"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>Làm mới</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex border-b border-[var(--color-border)]">
        <button
          type="button"
          onClick={() => setActiveTab('operational')}
          data-testid="tab-operational"
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
            activeTab === 'operational'
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-slate-800'
          }`}
        >
          <LayoutDashboard className="h-4 w-4" />
          <span>Dashboard Vận Hành</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('management')}
          data-testid="tab-management"
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
            activeTab === 'management'
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-slate-800'
          }`}
        >
          <LineChart className="h-4 w-4" />
          <span>Dashboard Quản Lý & Xu Hướng</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('detailed')}
          data-testid="tab-detailed"
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
            activeTab === 'detailed'
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-slate-800'
          }`}
        >
          <FileSpreadsheet className="h-4 w-4" />
          <span>Báo Cáo Chi Tiết</span>
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Tab Contents */}
      {activeTab === 'operational' && operationalData && (
        <OperationalDashboardTab data={operationalData} loading={loading} />
      )}

      {activeTab === 'management' && managementData && (
        <ManagementDashboardTab
          data={managementData}
          loading={loading}
          selectedCategory={selectedCategory}
        />
      )}

      {activeTab === 'detailed' && detailedData && (
        <DetailedReportsTab
          data={detailedData}
          selectedReportType={selectedDetailedType}
          onSelectReportType={(t) => {
            setSelectedDetailedType(t)
          }}
          loading={loading}
        />
      )}
    </div>
  )
}
